import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

// Fila de retry para não disparar múltiplos refreshes simultâneos
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
}

// Interceptor de requisição: anexa token CSRF automaticamente em métodos mutáveis
// (o cookie csrf_token não é HttpOnly — o frontend precisa lê-lo, LE-004)
api.interceptors.request.use((config) => {
  if (typeof document !== 'undefined') {
    const method = (config.method || 'get').toLowerCase();
    const csrfMatch = document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
    const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : null;

    // LE-023: FormData (upload de imagem) precisa ir como multipart/form-data com
    // boundary. O default do axios abaixo (application/json) faz o axios 1.7.x
    // serializar o FormData para JSON — o multer então não recebe o arquivo
    // (erro NO_FILE no upload de logo/capa/avatar pelo navegador). Remove o
    // Content-Type e deixa o navegador montar o multipart corretamente.
    if (config.data instanceof FormData) {
      const headers = { ...config.headers };
      delete headers['Content-Type'];
      config.headers = headers;
    }

    if (csrfToken && !['get', 'head', 'options'].includes(method)) {
      config.headers = { ...config.headers, 'X-CSRF-Token': csrfToken };
    }
  }
  return config;
});

// Interceptor de resposta: refresh automático em 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Só tenta refresh em 401 e quando não é o próprio endpoint de refresh/auth
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/register') &&
      !originalRequest.url.includes('/auth/refresh') &&
      !originalRequest.url.includes('/auth/csrf')
    ) {
      if (isRefreshing) {
        // Já está refrescando — enfileira
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Se o refresh falhou, o usuário precisa fazer login novamente
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('nc:session-expired'));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
